import { For, Show, createMemo } from 'solid-js';
import Text from '../Text';
import { useTranslation } from '../../hooks/useTranslation';
import { useCraftingResolver } from '../../services/craftingResolverContext';
import { useItemName } from '../../hooks/useItemName';
import { PALETTE_PRESETS, colourIds } from '../../mapart/palette';
import type { PalettePreset } from '../../mapart/palette';
import type { BlockSelection, MapartPalette, PaletteBlock } from '../../mapart/types';
import styles from './PalettePicker.module.css';

// ItemIdentifierPicker was the candidate here but it searches the whole item list; this picks one
// block per fixed map colour, so it stays a swatch plus a per-colour select of that colour's blocks.
interface Props {
    palette: MapartPalette;
    selection: BlockSelection;
    onChange: (colourId: number, blockIndex: number | null) => void;
    onPreset: (preset: PalettePreset) => void;
    onManagePresets: () => void;
}

export default function PalettePicker(props: Props) {
    const { t } = useTranslation();
    const resolver = useCraftingResolver();
    const { itemName } = useItemName();

    const ids = createMemo(() => colourIds(props.palette));
    const enabled = createMemo(() => ids().filter((id) => props.selection[id] !== null && props.selection[id] !== undefined).length);

    const blockLabel = (block: PaletteBlock) => {
        const def = resolver()?.getItemById?.(block.id, block.meta);
        return def ? itemName(def.key) : block.display;
    };

    return (
        <div class="v" style={{ gap: 'var(--spacing-md)' }}>
            <div class={styles.headerRow}>
                <span class={styles.groupLabel}><Text val="MAPART_PALETTE" /></span>
                <span class={styles.count}>{enabled()} / {ids().length}</span>
            </div>

            <div class={styles.presets}>
                <For each={PALETTE_PRESETS}>{(preset) => (
                    <button type="button" class="btn btn-secondary btn-sm" onClick={() => props.onPreset(preset.key)}>
                        {t(preset.localeKey)}
                    </button>
                )}</For>
                <button type="button" class="btn btn-secondary btn-sm" onClick={props.onManagePresets}>
                    <i class="fa-solid fa-sliders" /> <Text val="MAPART_PRESETS_MANAGE" />
                </button>
            </div>

            <div class={styles.list}>
                <For each={ids()}>{(id) => {
                    const colour = props.palette.colors[id];
                    const current = () => props.selection[id];
                    return (
                        <div class={styles.row} classList={{ [styles.rowOff]: current() === null || current() === undefined }}>
                            <div class={styles.swatch} title={colour.wikiName}>
                                <span style={{ background: rgb(colour.tonesRgb.dark) }} />
                                <span style={{ background: rgb(colour.tonesRgb.normal) }} />
                                <span style={{ background: rgb(colour.tonesRgb.light) }} />
                            </div>
                            <select
                                class={styles.select}
                                value={current() === null || current() === undefined ? '' : String(current())}
                                onChange={(e) => props.onChange(id, e.currentTarget.value === '' ? null : Number(e.currentTarget.value))}
                            >
                                <option value="">{t('MAPART_COLOUR_OFF')}</option>
                                <For each={colour.blocks}>{(block, index) => (
                                    <option value={String(index())}>{blockLabel(block)}</option>
                                )}</For>
                            </select>
                            <Show when={current() !== null && current() !== undefined && colour.blocks[current()!]?.flags.length}>
                                <span class={styles.flags}>{colour.blocks[current()!].flags.join(' ')}</span>
                            </Show>
                        </div>
                    );
                }}</For>
            </div>
        </div>
    );
}

function rgb(value: [number, number, number]) {
    return `rgb(${value[0]}, ${value[1]}, ${value[2]})`;
}
